import { useCallback, useEffect, useRef, useState } from 'react';
import type { PersonalTodoRow } from '../lib/models/personalTodos';
import {
  personalTodoSaveHint,
  type PersonalTodoEditFocusField
} from '../lib/personalTodoSaveHint';
import type { PersonalTodosVMActions } from '../lib/vm/usePersonalTodosVM';

export interface PersonalTodoEditSession {
  id: string;
  titleDraft: string;
  bodyDraft: string;
  titleEditing: boolean;
  savedFlash: boolean;
}

export interface RowEditSessionView {
  isOpen: boolean;
  titleEditing: boolean;
  titleDraft: string;
  bodyDraft: string;
  footerLabel: string;
}

function isDirty(session: PersonalTodoEditSession, row: PersonalTodoRow): boolean {
  return (
    session.titleDraft !== row.title ||
    session.bodyDraft !== (row.body ?? '')
  );
}

export function usePersonalTodoEditSession() {
  const [session, setSession] = useState<PersonalTodoEditSession | null>(null);
  const [focusField, setFocusField] = useState<PersonalTodoEditFocusField>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const discardSession = useCallback(() => {
    setSession(null);
    setFocusField(null);
  }, []);

  const startSession = useCallback((row: PersonalTodoRow) => {
    setSession({
      id: row.id,
      titleDraft: row.title,
      bodyDraft: row.body ?? '',
      titleEditing: false,
      savedFlash: false
    });
    setFocusField('body');
    requestAnimationFrame(() => {
      const ta = document.querySelector<HTMLTextAreaElement>(
        `[data-testid="pt-row-body-textarea-${row.id}"]`
      );
      ta?.focus();
    });
  }, []);

  const beginTitleEdit = useCallback(() => {
    setSession((cur) => {
      if (!cur || cur.titleEditing) return cur;
      return { ...cur, titleEditing: true, savedFlash: false };
    });
    setFocusField('title');
    requestAnimationFrame(() => {
      const id = sessionRef.current?.id;
      if (!id) return;
      const inp = document.querySelector<HTMLInputElement>(
        `[data-testid="pt-row-title-input-${id}"]`
      );
      if (inp) {
        inp.focus();
        inp.select();
      }
    });
  }, []);

  const setTitleFocus = useCallback(() => {
    setFocusField('title');
  }, []);

  const setBodyFocus = useCallback(() => {
    setFocusField('body');
  }, []);

  const handleTitleClick = useCallback(
    (row: PersonalTodoRow) => {
      if (!session || session.id !== row.id) {
        startSession(row);
        return;
      }
      if (!session.titleEditing) {
        beginTitleEdit();
      }
    },
    [session, startSession, beginTitleEdit]
  );

  const setTitleDraft = useCallback((next: string) => {
    setSession((cur) =>
      cur ? { ...cur, titleDraft: next, savedFlash: false } : cur
    );
  }, []);

  const setBodyDraft = useCallback((next: string) => {
    setSession((cur) =>
      cur ? { ...cur, bodyDraft: next, savedFlash: false } : cur
    );
  }, []);

  const saveSession = useCallback(
    async (saveContent: PersonalTodosVMActions['saveContent']) => {
      const cur = sessionRef.current;
      if (!cur) return;
      const ok = await saveContent(cur.id, {
        title: cur.titleDraft,
        body: cur.bodyDraft
      });
      if (!ok) return;
      setSession({
        id: cur.id,
        titleDraft: cur.titleDraft.trim() || 'Untitled todo',
        bodyDraft: cur.bodyDraft,
        titleEditing: false,
        savedFlash: true
      });
      setFocusField('body');
    },
    []
  );

  const viewForRow = useCallback(
    (row: PersonalTodoRow): RowEditSessionView | null => {
      if (!session || session.id !== row.id) return null;
      const dirty = isDirty(session, row);
      return {
        isOpen: true,
        titleEditing: session.titleEditing,
        titleDraft: session.titleDraft,
        bodyDraft: session.bodyDraft,
        footerLabel: personalTodoSaveHint({
          dirty,
          savedFlash: session.savedFlash,
          bodyDraft: session.bodyDraft,
          focusField
        })
      };
    },
    [session, focusField]
  );

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const cur = sessionRef.current;
      if (!cur) return;
      const row = document.querySelector(`[data-testid="pt-row-${cur.id}"]`);
      if (row && !row.contains(e.target as Node)) {
        discardSession();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [discardSession]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!sessionRef.current) return;
      e.preventDefault();
      discardSession();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [discardSession]);

  return {
    session,
    startSession,
    beginTitleEdit,
    handleTitleClick,
    setTitleDraft,
    setBodyDraft,
    setTitleFocus,
    setBodyFocus,
    saveSession,
    discardSession,
    viewForRow
  };
}
